/**
 * What the browser knows that the server cannot: the route the person was on,
 * the errors their tab logged, the requests that failed under them.
 *
 * A support ticket is only as good as what arrives with it. "Δεν δουλεύει" in
 * a textarea is a round trip and a guess; the same sentence with the last few
 * console errors, the failed request and the build sha is usually the answer.
 *
 * Two deliberate choices:
 *
 * Module scope, not React state. There was a `useConsoleCapture` hook doing
 * this with `setState` on every console call — a re-render per log line, for
 * a value nothing renders. A ring buffer written by a closure costs nothing
 * and is readable from anywhere, including from an error boundary that has
 * already lost its tree.
 *
 * Narrow by default. Only warnings and errors, never `console.log`; request
 * paths with the query string removed; no storage, no cookies, no headers, no
 * request bodies. Diagnostics get attached to a record a human reads later,
 * so anything collected "just in case" is a small permanent liability. If
 * something here is ever not enough, widening it is one line — and a decision
 * someone makes on purpose.
 */

export interface LogEntry {
  level: 'warn' | 'error';
  message: string;
  at: string;
}

export interface NetEntry {
  method: string;
  /** Path only — the query string is dropped before it is stored. */
  path: string;
  status: number | 'network-error';
  ms: number;
  at: string;
}

export interface Diagnostics {
  route: string;
  build: string;
  userAgent: string;
  viewport: string;
  language: string;
  /** IANA zone, which is how "it broke at 9pm" becomes a timestamp. */
  timezone: string;
  logs: LogEntry[];
  network: NetEntry[];
  collectedAt: string;
}

const MAX_LOGS = 25;
const MAX_NET = 15;

const logs: LogEntry[] = [];
const network: NetEntry[] = [];
let installed = false;

/** Keep a bounded tail; the newest failure is the one being asked about. */
function push<T>(buf: T[], entry: T, max: number) {
  buf.push(entry);
  if (buf.length > max) buf.splice(0, buf.length - max);
}

/** Arguments to a readable line, with Errors keeping their first stack frame. */
function render(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) {
        const frame = (a.stack || '').split('\n')[1]?.trim();
        return frame ? `${a.message} (${frame})` : a.message;
      }
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(' ')
    .slice(0, 500);
}

/**
 * A URL reduced to origin + path. Query strings carry tokens and emails, and
 * this is the rule that keeps them out of a record a human reads later, so it
 * is exported to be tested rather than trusted.
 */
export function safePath(input: RequestInfo | URL): string {
  const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url;

  // Resolving against an origin makes almost any string a valid path, so the
  // only real failure is having no string at all. Checking for that up front
  // is what makes the fallback reachable — written as a catch alone, it never
  // ran, and a `fetch(someObject)` would have been logged as "/undefined".
  if (typeof raw !== 'string' || raw === '') return '(unparseable)';

  try {
    const u = new URL(raw, window.location.origin);
    return u.origin === window.location.origin ? u.pathname : `${u.origin}${u.pathname}`;
  } catch {
    return '(unparseable)';
  }
}

/**
 * Start recording. Safe to call more than once; only the first call binds.
 * Call it as early as the app boots — the errors worth having are the ones
 * from before anybody thought to look.
 */
export function installDiagnostics(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  for (const level of ['warn', 'error'] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      push(logs, { level, message: render(args), at: new Date().toISOString() }, MAX_LOGS);
      original(...args);
    };
  }

  window.addEventListener('error', (e) => {
    push(logs, {
      level: 'error',
      message: `${e.message} @ ${e.filename?.split('/').pop() ?? '?'}:${e.lineno}`,
      at: new Date().toISOString(),
    }, MAX_LOGS);
  });

  // The failure mode that leaves no console trace of its own.
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    push(logs, {
      level: 'error',
      message: `Unhandled rejection: ${r instanceof Error ? r.message : String(r).slice(0, 300)}`,
      at: new Date().toISOString(),
    }, MAX_LOGS);
  });

  // Only failures are recorded. A log of every successful request is noise
  // that buries the one line that matters.
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const started = performance.now();
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    try {
      const res = await originalFetch(input, init);
      if (!res.ok) {
        push(network, {
          method,
          path: safePath(input),
          status: res.status,
          ms: Math.round(performance.now() - started),
          at: new Date().toISOString(),
        }, MAX_NET);
      }
      return res;
    } catch (err) {
      push(network, {
        method,
        path: safePath(input),
        status: 'network-error',
        ms: Math.round(performance.now() - started),
        at: new Date().toISOString(),
      }, MAX_NET);
      throw err;
    }
  };
}

/** The bundle, as of now. Cheap: everything is already in memory. */
export function collectDiagnostics(): Diagnostics {
  return {
    route: typeof window === 'undefined' ? '' : window.location.pathname + window.location.hash,
    build: __APP_BUILD__,
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    viewport: typeof window === 'undefined' ? '' : `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}x`,
    language: typeof navigator === 'undefined' ? '' : navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    logs: [...logs],
    network: [...network],
    collectedAt: new Date().toISOString(),
  };
}

/** For tests and for the "clear" action in a debug panel. */
export function resetDiagnostics(): void {
  logs.length = 0;
  network.length = 0;
}
